import React, { useEffect, useState, useRef, lazy, memo, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, ShieldCheckIcon, LockIcon, ZapIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
const trackUS = (_name: string, _price: number) => {
  // AddToCart/InitiateCheckout removed
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const ACCENT = '#FF6C00';
const ACCENT_GLOW = 'rgba(255,108,0,0.3)';
const BG = '#0B0B0B';
const GOLD = '#D4AF37';
// ─── Checkout URLs ────────────────────────────────────────────────────────────
const CHECKOUT_DIGITAL_COLLECTION = 'https://library.legendaryreads.com/cf/click/1';
const CHECKOUT_PREMIUM = 'https://library.legendaryreads.com/cf/click/2';
const CHECKOUT_COLLECTOR = 'https://library.legendaryreads.com/cf/click/3';
const BASE_PRICE = 59.97;
const BUMP1_PRICE = 35;
const BUMP2_PRICE_STANDALONE = 220;
const BUMP2_PRICE_WITH_BUMP1 = 185;
const BUMP1_ITEMS = ['Everything in the Complete Digital Collection', 'Back In Time — Premium Hardcover Edition', 'Delivery in 12 days'];
const BUMP2_ITEMS = ['Everything in the Complete Digital Collection', 'All 4 volumes in Paperback Edition', 'Back In Time Audiobook Experience', 'Hill Valley 1985 Limited Vintage Trucker Hat', "Flux Energy Core Collector's Framed Print — 10×10 Limited Edition", 'Time Machine Heritage Tote', '40 Years Travelling Through Time Commemorative Mug', 'Fast Delivery'];
// ─── Shared primitives ────────────────────────────────────────────────────────
const OrangeButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  small?: boolean;
}) => <a href={href} onClick={onClick} className="cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg" style={{
  minHeight: small ? '44px' : '56px',
  padding: small ? '10px 16px' : '16px 24px',
  fontSize: small ? '14px' : '17px',
  background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
  color: '#0B0B0B',
  fontFamily: 'Inter, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}} data-id="element-1128">
    {children}
  </a>;
const BumpItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-2" data-id="element-1129">
    <span style={{
    color: ACCENT,
    flexShrink: 0,
    fontSize: '9px',
    marginTop: '5px'
  }} data-id="element-1130">
      ✦
    </span>
    <span className="text-xs leading-snug" style={{
    color: 'rgba(245,245,245,0.78)',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-1131">
      {text}
    </span>
  </li>;
// ─── Image Lightbox (used in pricing bumps) ─────────────────────────────────
const ImageLightbox = ({
  images,
  startIndex,
  onClose
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => i === 0 ? images.length - 1 : i - 1);
  const next = () => setIdx(i => i === images.length - 1 ? 0 : i + 1);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-1132">
      <div className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-1133">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close" data-id="element-1134">
          <XIcon className="h-7 w-7" data-id="element-1135" />
        </button>
        <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{
        aspectRatio: '1/1'
      }} data-id="element-1136">
          <img src={images[idx]} alt={`Item ${idx + 1}`} className="w-full h-full object-contain" decoding="async" loading="lazy" data-id="element-1137" />
        </div>
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Previous" data-id="element-1138">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-1139" />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Next" data-id="element-1140">
          <ChevronRightIcon className="h-5 w-5" data-id="element-1141" />
        </button>
        <div className="flex justify-center gap-1.5 mt-3" data-id="element-1142">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className="rounded-full transition-all duration-200" style={{
          width: i === idx ? '20px' : '8px',
          height: '8px',
          backgroundColor: i === idx ? ACCENT : 'rgba(255,255,255,0.3)'
        }} data-id="element-1143" />)}
        </div>
      </div>
    </div>;
};
// ─── HEADER ──────────────────────────────────────────────────────────────────
const HeaderUS = () => <header className="bg-black text-white border-b border-orange-500/30 z-40 sticky top-0" data-id="element-1144">
    <div className="container mx-auto px-4 py-2.5 flex justify-between items-center" data-id="element-1145">
      <span className="font-bold text-lg bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text font-future" data-id="element-1146">
        Back In Time
      </span>
      <a href="#pricing-section" className="cta font-bold text-sm px-4 py-1.5 rounded-md transition-all hover:brightness-110" style={{
      background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
      color: '#0B0B0B',
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1147">
        Get My Copy
      </a>
    </div>
  </header>;
// ─── HERO SECTION ─────────────────────────────────────────────────────────
const HeroUS = () => <section className="py-6 md:py-10 relative overflow-hidden" style={{
  backgroundColor: BG
}} data-id="element-1148">
    <div className="absolute inset-0 opacity-20" style={{
    background: 'radial-gradient(ellipse at 70% 50%, rgba(255,108,0,0.15) 0%, transparent 70%)'
  }} data-id="element-1149" />
    <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10" data-id="element-1150">
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-1151">
        <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-1152">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.12em'
        }} data-id="element-1153">
            40th Anniversary Collector's Edition
          </p>
          <h1 className="text-[28px] md:text-5xl font-bold leading-tight mb-4 text-white font-future tracking-tight" data-id="element-1154">
            You've Seen It 50 Times.{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1155">
              You've Never Read Anything Like This.
            </span>
          </h1>
          <p className="text-sm md:text-lg text-white/75 leading-relaxed mb-5 max-w-xl mx-auto md:mx-0" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1156">
            The only collector's edition that goes beyond the film — into the
            production truth, the cultural legacy, and the reason this story
            lives in your bones 40 years later. Available in Premium Hardcover +
            Digital. The definitive collector's experience.
          </p>

          {/* Mobile image */}
          <div className="md:hidden mb-5 flex justify-center" data-id="element-1157">
            <div className="relative w-[85%]" data-id="element-1158">
              <div className="absolute -inset-1 rounded-xl blur-md opacity-40" style={{
              background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
            }} data-id="element-1159" />
              <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
              aspectRatio: '1/1'
            }} data-id="element-1160">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="400" height="400" data-id="element-1161" />
              </div>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2 justify-center md:justify-start mb-5" data-id="element-1162">
            <div className="flex gap-0.5" data-id="element-1163">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1164" />)}
            </div>
            <span className="text-white font-bold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1165">
              4.9
            </span>
            <span className="text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1166">
              (5,354+ Verified Reviews)
            </span>
          </div>

          <div className="max-w-md mx-auto md:mx-0" data-id="element-1167">
            <OrangeButton href="#pricing-section" data-id="element-1168">
              GET MY COPY NOW →
            </OrangeButton>
          </div>

          <div className="flex items-center justify-center md:justify-start gap-4 mt-4" data-id="element-1169">
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1170">
              <ShieldCheckIcon className="h-4 w-4 text-green-400" data-id="element-1171" />
              Secure Checkout
            </div>
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1172">
              <ZapIcon className="h-4 w-4 text-yellow-400" data-id="element-1173" />
              Free Shipping
            </div>
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1174">
              <LockIcon className="h-4 w-4 text-orange-400" data-id="element-1175" />
              One-Time Payment
            </div>
          </div>
        </div>

        {/* Right: desktop image */}
        <div className="hidden md:flex md:w-1/2 justify-center items-center" data-id="element-1176">
          <div className="relative w-full max-w-md" data-id="element-1177">
            <div className="absolute -inset-2 rounded-2xl blur-xl opacity-30" style={{
            background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
          }} data-id="element-1178" />
            <div className="relative rounded-2xl overflow-hidden border border-orange-500/30 shadow-[0_0_60px_rgba(255,108,0,0.2)]" style={{
            aspectRatio: '1/1'
          }} data-id="element-1179">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="600" height="600" data-id="element-1180" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── IDENTIFICATION BLOCK ─────────────────────────────────────────────────
const IdentificationUS = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1181">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-1182">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 font-future tracking-wide" data-id="element-1183">
        This Is For You If...
      </h2>

      <div className="space-y-4 mb-8" data-id="element-1184">
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1185">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1186">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1187">
            You still get chills when the lightning hits the clock tower — and
            you've never been able to fully explain why
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1188">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1189">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1190">
            You've rewatched it more times than you can count, and it still hits
            differently than anything made in the last 40 years
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1191">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1192">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1193">
            You've tried to put into words what made this film different — and
            realized no words were ever quite enough
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1194">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1195">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1196">
            You feel like something that genuinely shaped who you are deserves
            more than a Wikipedia page and a streaming algorithm
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1197">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1198">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1199">
            You want to finally understand — not just feel — why this story
            refuses to leave you alone
          </p>
        </div>
      </div>

      <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8 text-center italic" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1200">
        Most anniversary products treat you like a casual viewer. This one was
        built for the fan who already loves the film — and is ready to finally
        understand it.
      </p>

      <div className="text-center mb-8" data-id="element-1201">
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{
        color: ACCENT,
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1202">
          INTRODUCING
        </p>
        <h3 className="text-2xl md:text-3xl font-bold mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1203">
          BACK IN TIME
        </h3>
        <p className="text-white font-semibold text-lg md:text-xl mb-3" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1204">
          The 40th Anniversary Collector's Edition
        </p>
        <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1205">
          The most complete legacy document ever assembled for Back to the
          Future.
        </p>
      </div>

      <div className="flex justify-center" data-id="element-1206">
        <div className="relative w-full max-w-md" data-id="element-1207">
          <div className="absolute -inset-1 rounded-xl blur-md opacity-30" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-1208" />
          <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
          aspectRatio: '1/1'
        }} data-id="element-1209">
            <img src="https://cdn.magicpatterns.com/uploads/vmUveDzVZd8xfHCdUCQ517/PH.02.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" loading="lazy" decoding="async" width="500" height="500" data-id="element-1210" />
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── WHAT YOU GET STACK ────────────────────────────────────────────────────
const BOOK_IMAGES = ['https://cdn.magicpatterns.com/uploads/eRaLLFj1Xpd9yKh17SfpDw/BACK_IN_TIME_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/qWMGMZni8tSubnCQBBHeda/BEHIND_THE_CLOCK_TOWER_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/6N2oU2pwJLDZiEUA7vpeCu/GREAT_SCOTT_40_QUOTES_FOR_40_YEARS_COVER_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/8pvDLEzUAHUVNEPcPZcf74/TIME_IMAGINATION_DESTINY_-_COVER_DIGITAL.png'];
const BookImage = ({
  src,
  alt
}: {
  src: string;
  alt: string;
}) => <div className="flex-shrink-0 w-full md:w-[200px]" data-id="element-1211">
    <div className="mx-auto max-w-full md:max-w-[200px]" data-id="element-1212">
      <img src={src} alt={alt} className="rounded-lg w-full object-cover shadow-lg" style={{
      aspectRatio: '1/1'
    }} loading="lazy" decoding="async" data-id="element-1213" />
    </div>
  </div>;
const WhatYouGetUS = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1214">
    <div className="container mx-auto px-4 max-w-3xl" data-id="element-1215">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1216">
        What Makes This Different
      </h2>
      <p className="text-center text-white/50 text-sm mb-8" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1217">
        Four volumes. Every angle. Nothing left out.
      </p>

      <div className="space-y-6" data-id="element-1218">
        <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1219">
          <BookImage src={BOOK_IMAGES[0]} alt="Back In Time — The Cultural Analysis" data-id="element-1220" />
          <div className="flex-1" data-id="element-1221">
            <div className="flex items-center gap-2 mb-2" data-id="element-1222">
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
              backgroundColor: 'rgba(255,108,0,0.15)',
              color: ACCENT,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1223">
                BOOK 1
              </span>
            </div>
            <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1224">
              IT DOCUMENTS THE LEGACY — NOT THE PLOT
            </h3>
            <p className="text-white/65 text-sm leading-relaxed" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1225">
              190+ pages covering the cultural architecture of a story that's
              outlasted every trend, every franchise reboot, and every piece of
              technology it predicted. Why does it still feel alive? Why do
              complete strangers cry in the same place every time? Why do
              parents all over the world sit their kids down and press play like
              it's a rite of passage? This book answers all of it — with the
              depth the film has always deserved.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1226">
          <BookImage src={BOOK_IMAGES[1]} alt="Behind The Clock Tower" data-id="element-1227" />
          <div className="flex-1" data-id="element-1228">
            <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1229">
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
              backgroundColor: 'rgba(255,108,0,0.15)',
              color: ACCENT,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1230">
                BOOK 2
              </span>
            </div>
            <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1231">
              THE PRODUCTION TRUTH NOBODY PUBLISHED PROPERLY
            </h3>
            <p className="text-white/65 text-sm leading-relaxed" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1232">
              Michael J. Fox working 18-hour days — Family Ties from 10 AM to 6
              PM, then the BTTF set until 2 AM. The DeLorean that kept breaking
              down on night shoots, requiring industrial hairdryers to unfreeze
              its gull-wing doors between takes. The casting decision that
              nearly bankrupted the production and cost $4 million to fix. It's
              all here, documented in full.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1233">
          <BookImage src={BOOK_IMAGES[2]} alt="Great Scott!" data-id="element-1234" />
          <div className="flex-1" data-id="element-1235">
            <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1236">
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
              backgroundColor: 'rgba(255,108,0,0.15)',
              color: ACCENT,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1237">
                BOOK 3
              </span>
            </div>
            <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1238">
              40 VOICES. 40 YEARS. EVERY ONE COUNTS.
            </h3>
            <p className="text-white/65 text-sm leading-relaxed" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1239">
              Not just the famous quotes. The layered ones. The ones that hit
              completely differently on the 20th viewing than they did on the
              first. Each one contextualized, with the real-world resonance
              documented behind it. The book you read once, then return to.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1240">
          <BookImage src={BOOK_IMAGES[3]} alt="Time. Imagination. Destiny." data-id="element-1241" />
          <div className="flex-1" data-id="element-1242">
            <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1243">
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
              backgroundColor: 'rgba(255,108,0,0.15)',
              color: ACCENT,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1244">
                BOOK 4
              </span>
            </div>
            <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1245">
              THE PHILOSOPHY THAT EXPLAINS WHY IT STILL HAUNTS YOU
            </h3>
            <p className="text-white/65 text-sm leading-relaxed" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1246">
              Why does a movie about a teenager in a time machine still feel
              this important 40 years later? This volume goes deeper than any
              film review ever has — exploring what Back to the Future says
              about free will, identity, ambition, and the courage required to
              change your future. The one you won't stop thinking about after
              you read it.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── PRICING + ORDER BUMP ─────────────────────────────────────────────────────
const PurchaseUS = () => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [bump2Expanded, setBump2Expanded] = useState(false);
  const [bump1LightboxOpen, setBump1LightboxOpen] = useState(false);
  const [bump2LightboxOpen, setBump2LightboxOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const hasTrackedRef = useRef(false);
  const bump2Price = bump1 ? BUMP2_PRICE_WITH_BUMP1 : BUMP2_PRICE_STANDALONE;
  const total = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? bump2Price : 0);
  const checkoutUrl = bump2 ? CHECKOUT_COLLECTOR : bump1 ? CHECKOUT_PREMIUM : CHECKOUT_DIGITAL_COLLECTION;
  const handleCta = () => {
    const name = bump2 ? "Time Traveler's Collector Bundle" : bump1 ? 'Premium Hardcover Bundle' : 'Complete Digital Collection';
    trackUS(name, total);
  };
  const scrollToCta = () => {
    if (window.innerWidth < 768 && ctaRef.current) {
      setTimeout(() => {
        ctaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 50);
    }
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current && !sessionStorage.getItem('pricing_section_viewed')) {
          hasTrackedRef.current = true;
          sessionStorage.setItem('pricing_section_viewed', 'true');
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} id="pricing-section" style={{
    backgroundColor: BG,
    paddingTop: '16px',
    paddingBottom: '32px',
    scrollMarginTop: '80px'
  }} className="md:py-12" data-id="element-1247">
      <div className="container mx-auto px-3 md:px-10 max-w-2xl" data-id="element-1248">
        <div className="text-center mb-3 md:mb-10" data-id="element-1249">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3" style={{
          backgroundColor: 'rgba(255,108,0,0.15)',
          color: ACCENT,
          border: '1px solid rgba(255,108,0,0.3)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1250">
            🔥 40th Anniversary Launch Pricing
          </span>
          <h2 className="text-[22px] md:text-4xl font-bold text-center font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1251">
            Special 40th Anniversary Collector's Offer
          </h2>
          <p className="text-white/50 text-xs mt-2 max-w-sm mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1252">
            ⚠️ This price exists because it's the 40th anniversary. When the
            window closes, it closes.
          </p>
        </div>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)] overflow-hidden" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-1253">
          <div className="p-3 md:p-10" data-id="element-1254">
            <div className="flex justify-center mb-2 md:mb-6" data-id="element-1255">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '200px',
              width: '100%'
            }} loading="lazy" decoding="async" width="260" height="260" data-id="element-1256" />
            </div>

            {/* What's included */}
            <div className="mb-2 md:mb-5" data-id="element-1257">
              <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1 md:mb-2 text-center" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1258">
                Includes All 4 Digital Books:
              </p>
              <ul className="space-y-1 md:space-y-1.5 max-w-sm mx-auto" data-id="element-1259">
                <BumpItem text="Back In Time — 40th Anniversary Edition (190 pages)" data-id="element-1260" />
                <BumpItem text="Behind The Clock Tower — Stories From The Set (87 pages)" data-id="element-1261" />
                <BumpItem text="Great Scott! 40 Quotes For 40 Years (87 pages)" data-id="element-1262" />
                <BumpItem text="Time. Imagination. Destiny. — The Philosophical Legacy (71 pages)" data-id="element-1263" />
                <BumpItem text="Instant Download — All 4 Books" data-id="element-1264" />
              </ul>
            </div>

            <div className="text-center mb-2" data-id="element-1265">
              <span className="line-through opacity-40 mr-2 text-lg transition-all duration-300" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1266">
                ${(114.97 + (bump1 ? 68 : 0) + (bump2 ? 360 : 0)).toFixed(2)}
              </span>
              <span className="font-bold text-3xl transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1267">
                ${total.toFixed(2)}
              </span>
            </div>

            <div className="text-center mb-3 md:mb-6" data-id="element-1268">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-bold px-3 py-1.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1269">
                You Save $
                {(114.97 + (bump1 ? 68.0 : 0) + (bump2 ? 360.0 : 0) - total).toFixed(2)}
              </span>
            </div>

            <div ref={ctaRef} data-id="element-1270">
              <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-1271">
                {bump2 ? 'GET MY COLLECTOR BUNDLE →' : bump1 ? 'GET MY PREMIUM EDITION →' : 'GET MY COLLECTION →'}
              </OrangeButton>
            </div>

            {/* ── SHOP PAY INSTALLMENTS ── */}
            <div className="mt-2 md:mt-3 mb-1 md:mb-2 hidden md:block" data-id="element-1272">
              <div className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg" style={{
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)'
            }} data-id="element-1273">
                <span className="text-white/60 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1274">
                  or 4 interest-free payments of
                </span>
                <span className="text-white font-bold text-sm transition-all duration-300" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1275">
                  ${(total / 4).toFixed(2)}
                </span>
                <span className="text-white/60 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1276">
                  with
                </span>
                {/* Shop Pay logo */}
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-1277" />
              </div>
            </div>

            {/* ── PAYMENT METHODS ── */}
            <div className="hidden md:flex items-center justify-center gap-3 mt-2 mb-2 flex-wrap" data-id="element-1278">
              {/* Visa */}
              <div className="rounded px-2 py-1" style={{
              backgroundColor: 'rgba(255,255,255,0.08)'
            }} data-id="element-1279">
                <svg width="32" height="20" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1280">
                  <rect width="48" height="32" rx="4" fill="#1A1F71" data-id="element-1281" />
                  <path d="M20.5 21.5H17.5L19.5 10.5H22.5L20.5 21.5Z" fill="white" data-id="element-1282" />
                  <path d="M30 10.5L27.2 18L26.8 16.2L25.8 11.5C25.8 11.5 25.6 10.5 24.3 10.5H19.8L19.7 10.8C19.7 10.8 21.2 11.1 22.8 12.1L25.5 21.5H28.6L33.2 10.5H30Z" fill="white" data-id="element-1283" />
                  <path d="M35 21.5H37.8L35.3 10.5H32.8C31.7 10.5 31.4 11.3 31.4 11.3L27 21.5H30.1L30.7 19.8H34.5L35 21.5ZM31.6 17.5L33.2 13L34 17.5H31.6Z" fill="white" data-id="element-1284" />
                  <path d="M16.5 13L16.1 11.2C16.1 11.2 15.9 10.5 14.8 10.5H10.2L10.1 10.8C10.1 10.8 12.5 11.3 14.8 13.2L16.5 21.5H19.6L16.5 13Z" fill="white" data-id="element-1285" />
                </svg>
              </div>
              {/* Mastercard */}
              <div className="rounded px-2 py-1" style={{
              backgroundColor: 'rgba(255,255,255,0.08)'
            }} data-id="element-1286">
                <svg width="32" height="20" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1287">
                  <rect width="48" height="32" rx="4" fill="#252525" data-id="element-1288" />
                  <circle cx="19" cy="16" r="8" fill="#EB001B" data-id="element-1289" />
                  <circle cx="29" cy="16" r="8" fill="#F79E1B" data-id="element-1290" />
                  <path d="M24 9.8C25.8 11.3 27 13.5 27 16C27 18.5 25.8 20.7 24 22.2C22.2 20.7 21 18.5 21 16C21 13.5 22.2 11.3 24 9.8Z" fill="#FF5F00" data-id="element-1291" />
                </svg>
              </div>
              {/* Amex */}
              <div className="rounded px-2 py-1" style={{
              backgroundColor: 'rgba(255,255,255,0.08)'
            }} data-id="element-1292">
                <svg width="32" height="20" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1293">
                  <rect width="48" height="32" rx="4" fill="#2E77BC" data-id="element-1294" />
                  <text x="24" y="18" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" fontFamily="Inter, sans-serif" data-id="element-1295">
                    AMEX
                  </text>
                </svg>
              </div>
              {/* PayPal */}
              <div className="rounded px-2 py-1" style={{
              backgroundColor: 'rgba(255,255,255,0.08)'
            }} data-id="element-1296">
                <svg width="32" height="20" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1297">
                  <rect width="48" height="32" rx="4" fill="#003087" data-id="element-1298" />
                  <text x="24" y="18" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="Inter, sans-serif" data-id="element-1299">
                    PayPal
                  </text>
                </svg>
              </div>
              {/* Apple Pay */}
              <div className="rounded px-2 py-1" style={{
              backgroundColor: 'rgba(255,255,255,0.08)'
            }} data-id="element-1300">
                <svg width="32" height="20" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1301">
                  <rect width="48" height="32" rx="4" fill="#000" data-id="element-1302" />
                  <text x="24" y="18" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="Inter, sans-serif" data-id="element-1303">
                    {' '}
                    Pay
                  </text>
                </svg>
              </div>
              {/* Google Pay */}
              <div className="rounded px-2 py-1" style={{
              backgroundColor: 'rgba(255,255,255,0.08)'
            }} data-id="element-1304">
                <svg width="32" height="20" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1305">
                  <rect width="48" height="32" rx="4" fill="#fff" data-id="element-1306" />
                  <text x="24" y="18" textAnchor="middle" fill="#5F6368" fontSize="6.5" fontWeight="bold" fontFamily="Inter, sans-serif" data-id="element-1307">
                    GPay
                  </text>
                </svg>
              </div>
            </div>

            <p className="hidden md:block text-center text-white/40 text-xs mt-3" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1308">
              Digital books delivered instantly. Physical items ship within 12
              days.
            </p>
            <div className="hidden md:flex items-center justify-center gap-5 mt-3" data-id="element-1309">
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1310">
                <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-1311" /> Secure
                Checkout
              </div>
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1312">
                <LockIcon className="h-4 w-4 text-orange-400" data-id="element-1313" /> One-Time
                Payment
              </div>
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 1 ── */}
        <div className="rounded-xl mb-3 overflow-hidden transition-all duration-300" style={{
        border: bump1 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump1 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump1 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-1314">
          <div className="px-4 py-2" style={{
          backgroundColor: bump1 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-1315">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1316">
              ⚡ Special One-Time Offer
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1317">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const n = !bump1;
            setBump1(n);
            if (n) scrollToCta();
          }} data-id="element-1318">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump1 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1319">
                {bump1 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-1320" />}
              </div>
              <div data-id="element-1321">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1322">
                  Add The Premium Hardcover Edition{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-1323">
                    — Only +${BUMP1_PRICE} Today
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1324">
                  The definitive collector's experience.
                </p>
                <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2 py-1 rounded-full mt-1.5" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1325">
                  You Save $33
                </span>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-3" data-id="element-1326">
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setBump1LightboxOpen(true);
            }} data-id="element-1327">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-1328">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Premium Hardcover Edition" className="rounded-lg shadow-lg w-full h-full object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" width="130" height="130" data-id="element-1329" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-1330">
                    <div className="bg-black/50 rounded-full p-1.5" data-id="element-1331">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-1332">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-1333" />
                      </svg>
                    </div>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1334">
                  Tap to enlarge
                </p>
              </div>
              <ul className="space-y-1.5 flex-1" data-id="element-1335">
                {BUMP1_ITEMS.map(item => <BumpItem key={item} text={item} data-id="element-1336" />)}
              </ul>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump1 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const n = !bump1;
            setBump1(n);
            if (n) scrollToCta();
          }} data-id="element-1337">
              {bump1 ? '✓ Added to my order' : `Add Premium Hardcover — Only +${BUMP1_PRICE}`}
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 2 ── */}
        <div className="rounded-xl overflow-hidden transition-all duration-300" style={{
        border: bump2 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump2 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump2 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-1338">
          <div className="px-4 py-2" style={{
          backgroundColor: bump2 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-1339">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump2 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1340">
              🚀 The Ultimate Upgrade
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1341">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const n = !bump2;
            setBump2(n);
            if (n) scrollToCta();
          }} data-id="element-1342">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump2 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1343">
                {bump2 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-1344" />}
              </div>
              <div data-id="element-1345">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1346">
                  Time Traveler's Collector Bundle{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-1347">
                    — Only +${bump2Price} Today
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1348">
                  The complete physical collection + merch.
                </p>
                <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2 py-1 rounded-full mt-1.5" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1349">
                  You Save $140
                </span>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-3" data-id="element-1350">
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setBump2LightboxOpen(true);
            }} data-id="element-1351">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-1352">
                  <img src="https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png" alt="Collector Bundle" className="rounded-lg shadow-lg w-full h-full object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" width="130" height="130" data-id="element-1353" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-1354">
                    <div className="bg-black/50 rounded-full p-1.5" data-id="element-1355">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-1356">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-1357" />
                      </svg>
                    </div>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1358">
                  Tap to enlarge
                </p>
              </div>
              <div className="flex-1" data-id="element-1359">
                <ul className="space-y-1.5" data-id="element-1360">
                  {BUMP2_ITEMS.slice(0, bump2Expanded ? undefined : 3).map(item => <BumpItem key={item} text={item} data-id="element-1361" />)}
                </ul>
                {!bump2Expanded && BUMP2_ITEMS.length > 3 && <button onClick={e => {
                e.stopPropagation();
                setBump2Expanded(true);
              }} className="text-[10px] mt-2 flex items-center gap-1 hover:text-white transition-colors" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1362">
                    + {BUMP2_ITEMS.length - 3} more items{' '}
                    <ChevronDownIcon className="h-3 w-3" data-id="element-1363" />
                  </button>}
              </div>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump2 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump2 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const n = !bump2;
            setBump2(n);
            if (n) scrollToCta();
          }} data-id="element-1364">
              {bump2 ? '✓ Added to my order' : `Add Collector Bundle — Only +${bump2Price}`}
            </div>
          </div>
        </div>
      </div>

      {bump1LightboxOpen && <ImageLightbox images={['https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png']} startIndex={0} onClose={() => setBump1LightboxOpen(false)} data-id="element-1365" />}
      {bump2LightboxOpen && <ImageLightbox images={['https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png']} startIndex={0} onClose={() => setBump2LightboxOpen(false)} data-id="element-1366" />}
    </section>;
};
// ─── SOCIAL PROOF ─────────────────────────────────────────────────────────
const TESTIMONIALS_DATA = [{
  quote: "Finally. Something that actually respects the legacy. I've bought anniversary merch before. Keychains, Blu-rays with the same bonus features recycled every five years. This is different. Back In Time reads like it was written by someone who genuinely understands what this film meant — not just as entertainment, but as a cultural moment. I'm 52 years old and I still got chills reading the chapter on the clock tower sequence. Worth every penny.",
  name: 'David M., 52',
  location: 'Software Engineer | Portland, Oregon, USA'
}, {
  quote: "I cried reading 'Behind the Clock Tower.' Actual tears. I wasn't expecting to be emotional about a book on filmmaking. But when they described Michael J. Fox working 18-hour days between sets, the dedication these people had — it hit me hard. My dad took me to see this film in 1985. He passed three years ago. Reading this felt like honoring both of them.",
  name: 'Karen T., 58',
  location: 'Retired Teacher | Manchester, UK'
}, {
  quote: "My 16-year-old watched it with me after I showed her this collection. I've been trying to get my daughter to understand why Back to the Future isn't just 'an old movie.' After she read a few sections of Time. Imagination. Destiny. with me, she watched the trilogy start to finish. Unprompted. That's the power of this collection — it gives you the language to explain why this film matters to people who weren't there.",
  name: 'James R., 47',
  location: 'Architect | Toronto, Canada'
}, {
  quote: "The best anniversary release in a decade. No contest. I've been a BTTF fan my entire adult life and I'm deeply skeptical of anniversary marketing. Cash grabs everywhere. This is not that. Back In Time is genuinely researched, intelligently written, and emotionally honest. The philosophical analysis in Volume 4 alone is worth the price of the entire collection.",
  name: 'Michael B., 61',
  location: 'University Lecturer | Edinburgh, Scotland'
}, {
  quote: "Read it in one sitting. Couldn't stop. I downloaded the digital collection on a Friday night thinking I'd skim the first volume. Looked up at 2am and I was halfway through Volume 3. The production stories in Behind the Clock Tower are absolutely riveting — details I'd never read anywhere else. The DeLorean stories alone are worth it.",
  name: 'Rachel H., 44',
  location: 'Marketing Director | Sydney, Australia'
}, {
  quote: "This is the document I didn't know I needed. There's something strange about loving a film for 40 years and never being able to fully explain why. This collection gave me the vocabulary. The chapter on why the film resonates across generations isn't just insightful — it's validating. Like someone finally said out loud what we all felt but couldn't articulate.",
  name: 'Thomas W., 55',
  location: 'Small Business Owner | Chicago, Illinois, USA'
}, {
  quote: "Gifted the hardcover to my brother. He called me the next day. My brother and I saw Back to the Future together when we were kids. We both remembered exactly where we were sitting in the theater. I ordered the hardcover upgrade as his birthday gift, and he called me the day it arrived, genuinely emotional. We ended up talking for two hours about the film. Best gift I've given in years.",
  name: 'Sandra L., 50',
  location: 'Registered Nurse | Calgary, Canada'
}, {
  quote: "Skeptical at first. Completely wrong to be. I'll be honest — I almost didn't buy this because I've been burned before by anniversary content that turned out to be repackaged Wikipedia articles. This is not that. The writing is sharp, the research is deep, and the emotional framing throughout is genuine. Whoever wrote this actually loves the film.",
  name: 'Andrew F., 48',
  location: 'IT Project Manager | Melbourne, Australia'
}, {
  quote: "The section on the Eric Stoltz recasting alone is worth it. I'd heard the basic story before, but the level of detail in Behind the Clock Tower goes far beyond anything I'd read. The production decisions that almost killed the movie, the pressure on everyone involved — it reads like a thriller. And then the film comes out and becomes one of the greatest of all time. Remarkable story.",
  name: 'Christopher D., 53',
  location: 'Film Professor | Boston, Massachusetts, USA'
}, {
  quote: "I've rewatched the trilogy 20+ times. I learned things I never knew. That should tell you something about the depth of this collection. These aren't surface-level fun facts. The production history, the cultural analysis, the philosophical chapters — it's a serious, respectful treatment of a film that deserves exactly that. I'll be reading this again.",
  name: 'Fiona M., 46',
  location: 'Graphic Designer | Dublin, Ireland'
}, {
  quote: "My husband and I read it together. Best purchase we've made in months. We both grew up on this film but from different countries — he's American, I'm British. It was fascinating to compare how differently we each experienced the film culturally, and then read how universally it landed worldwide. Time. Imagination. Destiny. made us both feel seen in a way we didn't expect.",
  name: 'Patricia & Alan G., 56 & 59',
  location: 'Retired Educators | Phoenix, Arizona, USA'
}, {
  quote: "I was worried this would be AI-generated filler. It absolutely is not. That concern crossed my mind when I first saw it — I'm in my 50s, not naive. But two pages in, it was obvious this was written by people who know the film deeply and care about doing it justice. The specificity of the production details, the quality of the cultural analysis — this is real work. Real love for the film.",
  name: 'Robert K., 57',
  location: 'Civil Engineer | Denver, Colorado, USA'
}, {
  quote: "The 40 quotes in 'Great Scott!' made me laugh and cry on the same page. That's an achievement. Volume 3 is the emotional heart of the collection for me. Forty perspectives on forty years — some funny, some devastating, some deeply philosophical. The range of voices is incredible. I read it in one afternoon and felt strangely lighter afterward. Like something had been resolved.",
  name: 'Diane A., 62',
  location: 'Librarian | Auckland, New Zealand'
}, {
  quote: "This is what I wanted to give my son on his 18th birthday. He'd never seen Back to the Future. I sat him down, we watched all three films, and then I handed him the digital collection. He spent the next week reading it between classes. He texted me yesterday: 'Dad, I finally get why you love this movie so much.' That's the whole point, isn't it?",
  name: 'Mark S., 51',
  location: 'Sales Manager | Houston, Texas, USA'
}, {
  quote: "40 years later, this film still has more to say. So does this collection. I was 15 when I first saw it in the cinema. I'm 55 now. And reading Back In Time reminded me that genuine art doesn't expire — it deepens. This collection doesn't just celebrate the film. It argues for why it matters. And it makes that argument beautifully. I'm grateful someone did this properly.",
  name: 'Jonathan P., 55',
  location: 'Writer | Vancouver, Canada'
}, {
  quote: "I've collected BTTF merchandise for 20 years. Steelbooks, prints, the whole thing. This is the first time I've read something about this film that made me feel like the person who made it actually understood why it matters. Read the whole flagship book in one night.",
  name: 'Mark T., 47',
  location: 'Columbus, Ohio'
}, {
  quote: "My son is 13. He'd seen the trilogy twice but didn't really get why I treat it like it's sacred. I read him the section on why fathers show this film to their kids. He went quiet for a second and then said 'that's literally us right now, Dad.' That's worth more than any price tag.",
  name: 'Sandra R., 52',
  location: 'Austin, Texas'
}, {
  quote: "I went in skeptical, every 'definitive' BTTF product I've bought has been gorgeous packaging with nothing underneath. Behind The Clock Tower alone destroyed that pattern. The DeLorean hairdryer story, the Stoltz reshoot, the 18-hour Fox schedule, I've been a fan for 35 years and I didn't know any of it at this level of detail.",
  name: 'David M., 39',
  location: 'San Diego, California'
}];
const TestimonialsUS = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = scrollLeft - (x - startX) * 2;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = scrollLeft - (x - startX) * 2;
  };
  const handleEnd = () => setIsDragging(false);
  const duplicatedTestimonials = [...TESTIMONIALS_DATA, ...TESTIMONIALS_DATA];
  return <section className="py-8 md:py-12 overflow-hidden" style={{
    backgroundColor: BG
  }} data-id="element-1367">
      <div className="mb-8 px-4" data-id="element-1368">
        <h2 className="text-2xl md:text-3xl font-bold text-center font-future tracking-wide text-white" data-id="element-1369">
          What Real Fans Are Saying
        </h2>
      </div>
      <div className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing" onMouseDown={handleMouseDown} onMouseLeave={handleEnd} onMouseUp={handleEnd} onMouseMove={handleMouseMove} onTouchStart={handleTouchStart} onTouchEnd={handleEnd} onTouchMove={handleTouchMove} data-id="element-1370">
        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 z-10 pointer-events-none bg-gradient-to-r from-[#0B0B0B] to-transparent" data-id="element-1371" />
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 z-10 pointer-events-none bg-gradient-to-l from-[#0B0B0B] to-transparent" data-id="element-1372" />
        <div ref={scrollRef} className="flex overflow-x-hidden no-scrollbar" data-id="element-1373">
          <div className={`flex gap-5 w-max ${isDragging ? '' : 'animate-marquee'}`} data-id="element-1374">
            {duplicatedTestimonials.map((testimonial, index) => <div key={index} className="p-5 rounded-xl flex flex-col flex-shrink-0 w-[320px] md:w-[350px] select-none" style={{
            backgroundColor: '#111111',
            border: '1px solid rgba(255,108,0,0.15)'
          }} data-id="element-1375">
                <div className="flex gap-0.5 mb-3" data-id="element-1376">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1377" />)}
                </div>
                <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1378">
                  "{testimonial.quote}"
                </p>
                <div data-id="element-1379">
                  <p className="text-white font-semibold text-sm" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1380">
                    {testimonial.name}
                  </p>
                  <p className="text-white/40 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1381">
                    {testimonial.location}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// ─── FAQ ───────────────────────
const faqItemsUS = [{
  question: 'What formats are available?',
  answer: "The base collection includes all 4 books in digital format (PDF, instant download), compatible with all devices — phones, tablets, computers, and e-readers.\n\nYou can also add the Premium Hardcover Edition or the full Time Traveler's Collector Bundle (which includes physical paperback editions, audiobook, merchandise, and more)."
}, {
  question: 'How quickly will I receive my order?',
  answer: "Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.\n\nPhysical items (Premium Hardcover, Collector Bundle) are professionally printed and typically ship within 12 business days. You'll receive a tracking email once your order ships."
}, {
  question: 'Is this official/licensed content?',
  answer: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  question: 'What makes this different from other BTTF books?',
  answer: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  question: 'Can I gift this to someone?',
  answer: "Yes. For digital editions, the download link is sent to the email used at checkout — you can forward it to the recipient or purchase using their email address.\n\nFor physical editions, simply enter the recipient's shipping address at checkout."
}, {
  question: 'Are there future updates or additions?',
  answer: 'Digital edition buyers may receive updates if new content is added to the collection. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  question: 'Who wrote this book?',
  answer: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material, it was created intentionally for true fans.'
}, {
  question: 'Is this book generated entirely by AI?',
  answer: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  question: 'What if I have an issue with my order?',
  answer: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
const FAQItemUS = ({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => <div className="border rounded-lg overflow-hidden transition-colors duration-200" style={{
  borderColor: 'rgba(255,108,0,0.2)'
}} data-id="element-1382">
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 cursor-pointer" style={{
    backgroundColor: 'rgba(17,17,17,0.8)'
  }} aria-expanded={isOpen} data-id="element-1383">
      <span className="text-base font-semibold text-white pr-4" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1384">
        {question}
      </span>
      <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{
      color: ACCENT
    }} data-id="element-1385" />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1386">
      <div className="px-5 pb-5 pt-1" style={{
      backgroundColor: 'rgba(11,11,11,0.6)'
    }} data-id="element-1387">
        {answer.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm leading-relaxed mb-3 last:mb-0" style={{
        color: 'rgba(245,245,245,0.6)',
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1388">
            {paragraph}
          </p>)}
      </div>
    </div>
  </div>;
const FAQUS = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-1389">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-1390">
        <div className="text-center mb-8" data-id="element-1391">
          <h2 className="text-2xl md:text-3xl font-bold font-future text-white mb-2" data-id="element-1392">
            Questions? We've Got Answers.
          </h2>
          <div className="w-20 h-1 mx-auto rounded-full" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-1393" />
        </div>
        <div className="space-y-3" data-id="element-1394">
          {faqItemsUS.map((item, index) => <FAQItemUS key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-1395" />)}
        </div>
      </div>
    </section>;
};
// ─── SOCIAL PROOF NOTIFICATION ────────────────────────────────────────────────
const firstNamesUS = ['James', 'Michael', 'Robert', 'John', 'David', 'William', 'Richard', 'Joseph', 'Thomas', 'Christopher', 'Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen'];
const lastNamesUS = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Thompson', 'White', 'Harris', 'Clark', 'Lewis'];
const usCities = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'Austin, TX', 'Denver, CO', 'Portland, OR', 'Seattle, WA', 'Boston, MA', 'Nashville, TN', 'Atlanta, GA', 'Miami, FL', 'San Francisco, CA', 'Columbus, OH', 'Charlotte, NC'];
const productsUS = [{
  name: 'Complete Digital Collection',
  price: '$59.97',
  image: 'https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png'
}, {
  name: 'Premium Hardcover Edition Bundle',
  price: '$94.97',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png'
}, {
  name: "Time Traveler's Collector Bundle",
  price: '$279.97',
  image: 'https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png'
}];
const SocialProofNotificationUS = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<any>(null);
  const generateRandomNotification = () => {
    const firstName = firstNamesUS[Math.floor(Math.random() * firstNamesUS.length)];
    const lastName = lastNamesUS[Math.floor(Math.random() * lastNamesUS.length)];
    const product = productsUS[Math.floor(Math.random() * productsUS.length)];
    const locationStr = usCities[Math.floor(Math.random() * usCities.length)];
    return {
      name: `${firstName} ${lastName}`,
      city: locationStr,
      product: product.name,
      price: product.price,
      image: product.image
    };
  };
  useEffect(() => {
    const getRandomInterval = () => Math.floor(Math.random() * 2000) + 12000;
    const showNotification = () => {
      setCurrentNotification(generateRandomNotification());
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 4000);
    };
    const initialDelay = setTimeout(() => showNotification(), 12000);
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, getRandomInterval());
    };
    scheduleNext();
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(timeoutId);
    };
  }, []);
  if (!currentNotification) return null;
  return <div className={`fixed z-30 transition-all duration-500 ease-out bottom-4 left-1/2 -translate-x-1/2 md:bottom-5 md:left-5 md:translate-x-0 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`} data-id="element-1396">
      <div className="relative backdrop-blur-md rounded-lg border shadow-[0_4px_20px_rgba(0,0,0,0.5)] p-3 flex items-center gap-3 w-[260px] sm:w-[280px]" style={{
      backgroundColor: 'rgba(11,11,11,0.95)',
      borderColor: 'rgba(255,108,0,0.3)'
    }} data-id="element-1397">
        <button onClick={() => setIsVisible(false)} className="absolute top-1.5 right-1.5 transition-colors z-10" style={{
        color: 'rgba(245,245,245,0.6)'
      }} aria-label="Close notification" data-id="element-1398">
          <XIcon className="h-3 w-3" data-id="element-1399" />
        </button>
        <div className="flex-shrink-0" data-id="element-1400">
          <div className="w-12 h-12 rounded-md overflow-hidden border border-orange-500/20" data-id="element-1401">
            <img src={currentNotification.image} alt="Book" className="w-full h-full object-cover" data-id="element-1402" />
          </div>
        </div>
        <div className="flex-1 min-w-0 pr-4" data-id="element-1403">
          <div className="flex items-center gap-1 mb-0.5" data-id="element-1404">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-id="element-1405"></div>
            <span className="text-[10px] font-semibold text-green-400 uppercase tracking-wide" data-id="element-1406">
              Just Purchased
            </span>
          </div>
          <p className="text-xs font-semibold text-white truncate" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1407">
            {currentNotification.name}
          </p>
          <p className="text-[10px] truncate" style={{
          color: 'rgba(245,245,245,0.6)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1408">
            {currentNotification.city}
          </p>
          <p className="text-[10px] font-semibold mt-0.5" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1409">
            {currentNotification.product}
          </p>
        </div>
      </div>
    </div>;
};
// ─── LANDING US EXPORT ────────────────────────────────────────────────────────
export const LandingUS = () => <div className="w-full min-h-screen" style={{
  backgroundColor: BG,
  color: '#F5F5F5'
}} data-id="element-1410">
    <HeaderUS data-id="element-1411" />
    <main data-id="element-1412">
      <HeroUS data-id="element-1413" />
      <IdentificationUS data-id="element-1414" />
      <WhatYouGetUS data-id="element-1415" />
      <TestimonialsUS data-id="element-1416" />
      <PurchaseUS data-id="element-1417" />
      <FAQUS data-id="element-1418" />
    </main>
    <Footer data-id="element-1419" />
    <SocialProofNotificationUS data-id="element-1420" />
  </div>;